import React, { useState, useEffect } from 'react';
import { X, AlertCircle } from 'lucide-react';

const AddPaymentModal = ({
  showAddForm,
  newPayment,
  setNewPayment,
  licenses,
  addPaymentLoading,
  addPaymentError,
  handleAddPayment,
  handleCloseAddForm
}) => {
  const [validationErrors, setValidationErrors] = useState({});
  
  // Reset validation errors when modal closes
  useEffect(() => {
    if (!showAddForm) {
      setValidationErrors({});
    }
  }, [showAddForm]);

  if (!showAddForm) return null;

  // Validate paid amount
  const validatePaidAmount = (paidAmount, totalAmount) => {
    const errors = {};
    
    if (paidAmount && totalAmount) {
      const paid = parseFloat(paidAmount);
      const total = parseFloat(totalAmount);
      
      if (paid > total) {
        errors.paid_amount = 'Paid amount cannot exceed total amount';
      }
      
      if (paid < 0) {
        errors.paid_amount = 'Paid amount cannot be negative';
      }
    }
    
    if (totalAmount && parseFloat(totalAmount) < 0) {
      errors.total_amount = 'Total amount cannot be negative';
    }
    
    return errors;
  };

  // Handle total amount change
  const handleTotalAmountChange = (e) => {
    const value = e.target.value;
    setNewPayment(prev => ({ ...prev, total_amount: value }));
    
    // Validate paid amount against new total amount
    const errors = validatePaidAmount(newPayment.paid_amount, value);
    setValidationErrors(errors);
  };

  // Handle paid amount change
  const handlePaidAmountChange = (e) => {
    const value = e.target.value;
    setNewPayment(prev => ({ ...prev, paid_amount: value }));
    
    // Validate paid amount
    const errors = validatePaidAmount(value, newPayment.total_amount);
    setValidationErrors(errors);
  };

  // Check if form is valid
  const isFormValid = () => {
    return (
      newPayment.license_id && 
      newPayment.total_amount && 
      parseFloat(newPayment.total_amount) >= 0 &&
      (!newPayment.paid_amount || 
       (parseFloat(newPayment.paid_amount) >= 0 && 
        parseFloat(newPayment.paid_amount) <= parseFloat(newPayment.total_amount)))
    );
  };

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate before submit
    const errors = validatePaidAmount(newPayment.paid_amount, newPayment.total_amount);
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    handleAddPayment(e);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold text-gray-800">Add New Payment</h2>
          <button 
            onClick={handleCloseAddForm}
            disabled={addPaymentLoading}
            className="disabled:opacity-50 text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">License *</label>
              <select
                name="license_id"
                value={newPayment.license_id}
                onChange={(e) => setNewPayment(prev => ({ ...prev, license_id: e.target.value }))}
                required
                disabled={addPaymentLoading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              >
                <option value="">Select License</option>
                {licenses.map(license => (
                  <option key={license.license_id} value={license.license_id}>
                    {license.license_key} (ID: {license.license_id})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Payment Plan</label>
              <select
                name="payment_plan"
                value={newPayment.payment_plan}
                onChange={(e) => setNewPayment(prev => ({ ...prev, payment_plan: e.target.value }))}
                disabled={addPaymentLoading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              >
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
                <option value="onetime">One Time</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Total Amount (LKR) *</label>
              <div className="relative">
                <input
                  type="number"
                  name="total_amount"
                  value={newPayment.total_amount}
                  onChange={handleTotalAmountChange}
                  required
                  disabled={addPaymentLoading}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 ${
                    validationErrors.total_amount ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter total amount"
                  step="0.01"
                  min="0"
                />
                {/* <div className="absolute right-3 top-2.5 text-gray-400">
                  LKR
                </div> */}
              </div>
              {validationErrors.total_amount && (
                <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                  <AlertCircle size={12} />
                  {validationErrors.total_amount}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Paid Amount (LKR)</label>
              <div className="relative">
                <input
                  type="number"
                  name="paid_amount"
                  value={newPayment.paid_amount}
                  onChange={handlePaidAmountChange}
                  disabled={addPaymentLoading}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 ${
                    validationErrors.paid_amount ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter paid amount"
                  step="0.01"
                  min="0"
                  max={newPayment.total_amount || undefined}
                />
                {/* <div className="absolute right-3 top-2.5 text-gray-400">
                  LKR
                </div> */}
              </div>
              {validationErrors.paid_amount ? (
                <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                  <AlertCircle size={12} />
                  {validationErrors.paid_amount}
                </p>
              ) : newPayment.total_amount && newPayment.paid_amount ? (
                <div className="text-xs mt-1">
                  <span className="text-gray-600">Remaining: </span>
                  <span className="font-medium">
                    LKR {(parseFloat(newPayment.total_amount) - parseFloat(newPayment.paid_amount)).toFixed(2)}
                  </span>
                  {parseFloat(newPayment.paid_amount) > parseFloat(newPayment.total_amount) && (
                    <span className="text-red-500 ml-2">(Exceeds total!)</span>
                  )}
                </div>
              ) : null}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
              <select
                name="payment_method"
                value={newPayment.payment_method}
                onChange={(e) => setNewPayment(prev => ({ ...prev, payment_method: e.target.value }))}
                disabled={addPaymentLoading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              >
                <option value="Cash">Cash</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Credit Card">Credit Card</option>
                <option value="Online">Online</option>
                <option value="Cheque">Cheque</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Remarks</label>
              <textarea
                name="remarks"
                value={newPayment.remarks}
                onChange={(e) => setNewPayment(prev => ({ ...prev, remarks: e.target.value }))}
                disabled={addPaymentLoading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                rows="3"
                placeholder="Enter payment remarks"
              />
            </div>
          </div>

          {/* Validation summary */}
          {Object.keys(validationErrors).length > 0 && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 text-red-700 mb-2">
                <AlertCircle size={16} />
                <span className="font-medium">Validation Errors:</span>
              </div>
              <ul className="text-sm text-red-600 list-disc pl-5 space-y-1">
                {Object.values(validationErrors).map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {addPaymentError && (
            <div className="p-2 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
              <strong>API Error:</strong> {addPaymentError}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleCloseAddForm}
              disabled={addPaymentLoading}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={addPaymentLoading || !isFormValid()}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-not-allowed font-medium"
            >
              {addPaymentLoading ? 'Adding...' : 'Add Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPaymentModal;